import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { observer, inject } from 'mobx-react'
import useClickOutside from 'components/common/useClickOutside'
import DeleteItemPrompt from 'components/tools/item/DeleteItemPrompt'

const UserShapefile = ({
  userShapefileList,
  shapefile,
  uiStore,
  onSelect
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  useClickOutside([buttonRef, menuRef], () => setMenuOpen(false), menuOpen)

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const menuWidth = 140
    const menuHeight = 44
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < menuHeight + 10 && rect.top > menuHeight

    const top = openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4
    let left = rect.right - menuWidth
    if (left < 8) left = 8
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8
    }

    setMenuPosition({ top, left })
  }, [])

  const handleToggleMenu = (e) => {
    e.stopPropagation()
    if (!menuOpen) {
      updateMenuPosition()
      setMenuOpen(true)
    } else {
      setMenuOpen(false)
    }
  }

  useEffect(() => {
    if (!menuOpen) return

    const handleScrollOrResize = () => setMenuOpen(false)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  const handleRowClick = async (e) => {
    // Prevent triggering row select when clicking the three-dot menu button or its dropdown
    if (
      e.target.closest('.shape-file-item__menu-wrap') ||
      e.target.closest('.shape-file-item__menu')
    ) {
      return
    }
    if (onSelect) {
      onSelect(shapefile)
    } else {
      uiStore.processing = true
      await shapefile.loadLevels()
      userShapefileList.selectedFile = shapefile
      uiStore.processing = false
    }
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    uiStore.openDeletePrompt({
      style: { width: 320 },
      selector: '.shape-file-item__menu-wrap',
      text: (
        <DeleteItemPrompt
          item={userShapefileList}
          itemName={`shapefile: ${shapefile.displayName}`}
          onSubmit={async () => {
            await shapefile.deleteShapefile()
            await userShapefileList.load()
          }}
        />
      )
    })
  }

  return (
    <li className='shape-file-item' onClick={handleRowClick}>
      <div className='shape-file-item__left'>
        <svg
          className='shape-file-item__icon'
          width='18'
          height='18'
          viewBox='0 0 24 24'
          fill='none'
          stroke='#64748B'
          strokeWidth='1.75'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <polygon points='12 2 2 7 12 12 22 7 12 2' />
          <polyline points='2 17 12 22 22 17' />
          <polyline points='2 12 12 17 22 12' />
        </svg>
        <span className='shape-file-item__name' title={shapefile.displayName}>
          {shapefile.displayName}
        </span>
      </div>
      <div className='shape-file-item__menu-wrap'>
        <button
          ref={buttonRef}
          type='button'
          className={`shape-file-item__menu-btn ${menuOpen ? 'is-active' : ''}`}
          onClick={handleToggleMenu}
          aria-label='File actions'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#94A3B8' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='12' cy='12' r='1.2' fill='#94A3B8' />
            <circle cx='19' cy='12' r='1.2' fill='#94A3B8' />
            <circle cx='5' cy='12' r='1.2' fill='#94A3B8' />
          </svg>
        </button>
        {menuOpen && createPortal(
          <div
            ref={menuRef}
            className='shape-file-item__menu'
            style={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              zIndex: 9999
            }}
          >
            <button
              type='button'
              className='shape-file-item__menu-action shape-file-item__menu-action--delete'
              onClick={handleDelete}
            >
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#EF4444' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='3 6 5 6 21 6' />
                <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' />
              </svg>
              <span>Delete file</span>
            </button>
          </div>,
          document.body
        )}
      </div>
    </li>
  )
}

export default inject('uiStore')(observer(UserShapefile))
